/**
 * Score display bands — THE single source of truth for every surface that
 * renders a 0–100 screening score as a band bar: the dashboard group cards,
 * and (when they migrate) the individual/PDF reports. Seed of Epic TT-40
 * (Score UI v2). Client-safe, no runtime deps.
 *
 * Ranges (client 2026-07-22, superseding the provisional 0–39/40–69/70–100):
 * red 0–49 / yellow 50–79 / green 80–100 — so a score under 50 reads red, 50
 * (inclusive) through 79 yellow, 80+ green. Refining the bands later means
 * editing THIS array only — every consumer derives from it (the band bar, the
 * numeric gauge, dashboard/listing/pipeline, and both PDFs).
 *
 * Rendering rule: the band containing the score is ACTIVE (saturated); the
 * others render faded. No score yet (pending / not computed) → no active
 * band, all faded. Pre-screening applications never show a score at all
 * (locked client decision — the caller hides the whole bar, not this module).
 *
 * Numeric mode (per-landlord `score_display` setting, superadmin-controlled):
 * when a tenant has "Show numeric score" enabled, surfaces render the raw
 * `NN/100` (value tinted with the active band's textClass) INSTEAD of the
 * band bar — see ScoreDisplay.tsx. The band-bar-only rule above remains the
 * default for every tenant without the setting.
 */

export interface ScoreBandDef {
	key: string
	/** Inclusive bounds on the rounded 0–100 score. */
	min: number
	max: number
	/** Band meaning, shown as the pill tooltip (client wording 2026-07-08). */
	label: string
	/** Tailwind class when this band is the active one. */
	activeClass: string
	/** Tailwind class when inactive (faded pastel of the same hue). */
	fadedClass: string
	/** Text color of the numeric score when this band is active (same hue). */
	textClass: string
	/** Raw colors for non-DOM renderers (react-pdf) — keep in sync with the
	 * Tailwind classes above. */
	hex: string
	fadedHex: string
}

export const SCORE_BAND_DEFS: ReadonlyArray<ScoreBandDef> = [
	{
		key: 'low',
		min: 0,
		max: 49,
		label: 'Avg Applicant',
		activeClass: 'bg-[#DC2634]',
		fadedClass: 'bg-[#FBEAE5]',
		textClass: 'text-[#DC2634]',
		hex: '#DC2634',
		fadedHex: '#FBEAE5',
	},
	{
		key: 'mid',
		min: 50,
		max: 79,
		label: 'Okay Applicant',
		activeClass: 'bg-[#F59F0A]',
		fadedClass: 'bg-[#FBF2DB]',
		textClass: 'text-[#F59F0A]',
		hex: '#F59F0A',
		fadedHex: '#FBF2DB',
	},
	{
		key: 'high',
		min: 80,
		max: 100,
		label: 'Good Applicant',
		activeClass: 'bg-[#16A34A]',
		fadedClass: 'bg-[#E8F5EC]',
		textClass: 'text-[#16A34A]',
		hex: '#16A34A',
		fadedHex: '#E8F5EC',
	},
]

/**
 * Guarantor bands: a guarantor is judged on ACCEPTABILITY (boolean from the
 * guarantor AI report), not a 0–100 score — so exactly TWO bands. Same
 * rendering/tooltip contract as the applicant defs; shared by the web and
 * PDF band bars.
 */
export interface GuarantorBandDef {
	key: string
	label: string
	activeClass: string
	fadedClass: string
	hex: string
	fadedHex: string
}

export const GUARANTOR_BAND_DEFS: ReadonlyArray<GuarantorBandDef> = [
	{
		key: 'not_acceptable',
		label: 'Not Acceptable',
		activeClass: 'bg-[#DC2634]',
		fadedClass: 'bg-[#FBEAE5]',
		hex: '#DC2634',
		fadedHex: '#FBEAE5',
	},
	{
		key: 'acceptable',
		label: 'Acceptable',
		activeClass: 'bg-[#16A34A]',
		fadedClass: 'bg-[#E8F5EC]',
		hex: '#16A34A',
		fadedHex: '#E8F5EC',
	},
]

/** null = no verdict yet (all bands faded). */
export function deriveActiveGuarantorBandIndex(
	acceptable: boolean | null | undefined,
): number | null {
	if (acceptable === null || acceptable === undefined) return null
	return acceptable ? 1 : 0
}

/**
 * Index into SCORE_BAND_DEFS of the band containing `score`, or null when
 * there is no usable score (pending state — every band renders faded).
 * Scores are rounded then clamped into 0–100 so out-of-range values from
 * upstream data can never produce a bandless bar.
 */
export function deriveActiveScoreBandIndex(
	score: number | null | undefined,
): number | null {
	if (score == null || !Number.isFinite(score)) return null
	const clamped = Math.min(100, Math.max(0, Math.round(score)))
	const index = SCORE_BAND_DEFS.findIndex(
		(band) => clamped >= band.min && clamped <= band.max,
	)
	return index === -1 ? null : index
}

/**
 * Text color class (and hex, for the PDF renderer) of the numeric score in
 * numeric display mode — the active band's hue. Null when there is no usable
 * score (callers render the pending band bar instead of a number).
 */
export function deriveScoreTextStyle(
	score: number | null | undefined,
): { textClass: string; hex: string } | null {
	const index = deriveActiveScoreBandIndex(score)
	if (index === null) return null
	const band = SCORE_BAND_DEFS[index]
	return { textClass: band.textClass, hex: band.hex }
}
